// services/MLPGameManager.js
export class MLPGameManager {
  constructor(teamData) {
    this.teamData = teamData;
    this.games = this.generateAllGames();
    this.gameResults = {};
    this.playerStatus = {};
    this.championshipFinal = {
      initialized: false,
      completed: false,
      team1: null,
      team2: null,
      currentPairs: null,
      score: { team1: 0, team2: 0 },
      winner: null,
      gameTypes: ['FF', 'MM', 'MX1', 'MX2'],
      currentGameTypeIndex: 0,
      pointsUntilRotation: 4
    };
    
    this.initializePlayerStatus();
  }

  // Generate all 24 predetermined MLP games
  generateAllGames() {
    const teams = Object.keys(this.teamData);
    const games = [];
    let gameId = 1;

    // Game types for each matchup
    const gameTypes = [
      { type: 'MM', label: "Men's Doubles", players: ['m1', 'm2'] },
      { type: 'FF', label: "Women's Doubles", players: ['f1', 'f2'] },
      { type: 'MX1', label: 'Mixed Doubles (M1+F1)', players: ['m1', 'f1'] },
      { type: 'MX2', label: 'Mixed Doubles (M2+F2)', players: ['m2', 'f2'] }
    ];

    // Generate all team combinations (6 total matchups)
    for (let i = 0; i < teams.length; i++) {
      for (let j = i + 1; j < teams.length; j++) {
        const team1 = teams[i];
        const team2 = teams[j];

        // Generate 4 games for each team matchup - FIXED: Create game immediately
        gameTypes.forEach(gameType => {
          const team1Players = gameType.players.map(pos => this.teamData[team1][pos]);
          const team2Players = gameType.players.map(pos => this.teamData[team2][pos]);

          const currentGameId = gameId; // FIXED: Capture gameId value immediately
          games.push({
            id: currentGameId,
            team1,
            team2,
            gameType: gameType.type,
            gameTypeLabel: gameType.label,
            players: {
              team1: team1Players,
              team2: team2Players
            },
            status: 'pending', // pending, in-progress, completed
            result: null,
            courtId: null,
            startTime: null,
            endTime: null,
            priorityScore: this.calculateGamePriority(team1, team2, gameType.type)
          });
          gameId++; // Increment after using the value
        });
      }
    }

    console.log(`Generated ${games.length} MLP games for tournament`);
    return games;
  }

  // Calculate priority score for game selection optimization
  calculateGamePriority(team1, team2, gameType) {
    // Higher priority for games with:
    // 1. Teams that have played fewer games
    // 2. Game types that are behind in the rotation
    // 3. Balanced matchups
    
    let priority = 100;
    
    // Adjust based on game type distribution
    const gameTypeWeights = {
      'MM': 1.0,
      'FF': 1.0, 
      'MX1': 1.1,
      'MX2': 1.1
    };
    
    priority *= (gameTypeWeights[gameType] || 1.0);
    
    // Add some randomization to avoid predictable patterns
    priority += Math.random() * 10;
    
    return Math.round(priority);
  }

  // Initialize player status tracking
  initializePlayerStatus() {
    Object.values(this.teamData).forEach(team => {
      [team.m1, team.m2, team.f1, team.f2].forEach(player => {
        const playerName = player.split('(')[0].trim();
        this.playerStatus[playerName] = {
          status: 'waiting', // waiting, playing, finished
          gamesPlayed: 0,
          currentCourt: null,
          lastFinished: null
        };
      });
    });
  }

  // FIXED: Get next game that only uses available players
  suggestNextMatchFromAvailable(availablePlayers) {
    const pendingGames = this.getGamesByStatus('pending');
    
    console.log('=== FINDING NEXT MATCH ===');
    console.log('Available players:', availablePlayers);
    console.log('Pending games to check:', pendingGames.length);
    
    for (const game of pendingGames) {
      const gamePlayerNames = [
        ...game.players.team1.map(player => player.split('(')[0].trim()),
        ...game.players.team2.map(player => player.split('(')[0].trim())
      ];
      
      // Check if ALL players in this game are available
      const allPlayersAvailable = gamePlayerNames.every(player => 
        availablePlayers.includes(player)
      );
      
      if (allPlayersAvailable) {
        console.log(`✓ Found valid game ${game.id}: ${game.team1} vs ${game.team2} (${game.gameTypeLabel})`);
        console.log(`  Players: ${gamePlayerNames.join(', ')}`);
        return game;
      } else {
        const unavailablePlayers = gamePlayerNames.filter(player => !availablePlayers.includes(player));
        console.log(`✗ Game ${game.id} invalid: Players not available: ${unavailablePlayers.join(', ')}`);
      }
    }
    
    console.log('✗ No valid games found with current available players');
    return null;
  }

  // Legacy method - still used for compatibility but should use suggestNextMatchFromAvailable
  suggestNextMatch(finishedPlayers, waitingPlayers) {
    const availablePlayers = [...finishedPlayers, ...waitingPlayers];
    return this.suggestNextMatchFromAvailable(availablePlayers);
  }

  // Get games by status
  getGamesByStatus(status) {
    return this.games.filter(game => game.status === status)
                    .sort((a, b) => b.priorityScore - a.priorityScore);
  }

  // Assign game to court
  assignGameToCourt(gameId, courtId) {
    const game = this.games.find(g => g.id === gameId);
    if (game) {
      game.status = 'in-progress';
      game.courtId = courtId;
      game.startTime = Date.now();
      
      // Update player status
      const allPlayers = [
        ...game.players.team1.map(p => p.split('(')[0].trim()),
        ...game.players.team2.map(p => p.split('(')[0].trim())
      ];
      
      allPlayers.forEach(playerName => {
        if (this.playerStatus[playerName]) {
          this.playerStatus[playerName].status = 'playing';
          this.playerStatus[playerName].currentCourt = courtId;
        }
      });
      
      console.log(`Game ${gameId} assigned to Court ${courtId}`);
      return true;
    }
    return false;
  }

  // Record game result
  recordGameResult(gameId, team1Score, team2Score) {
    const game = this.games.find(g => g.id === gameId);
    if (!game) return false;

    const winner = team1Score > team2Score ? game.team1 : game.team2;
    const loser = team1Score > team2Score ? game.team2 : game.team1;

    game.result = {
      team1Score,
      team2Score,
      winner,
      loser,
      pointDifferential: Math.abs(team1Score - team2Score)
    };
    
    game.status = 'completed';
    game.endTime = Date.now();

    // Update player status
    const allPlayers = [
      ...game.players.team1.map(p => p.split('(')[0].trim()),
      ...game.players.team2.map(p => p.split('(')[0].trim())
    ];
    
    allPlayers.forEach(playerName => {
      if (this.playerStatus[playerName]) {
        this.playerStatus[playerName].status = 'waiting';
        this.playerStatus[playerName].currentCourt = null;
        this.playerStatus[playerName].gamesPlayed++;
        this.playerStatus[playerName].lastFinished = Date.now();
      }
    });

    console.log(`Game ${gameId} completed: ${game.team1} ${team1Score}-${team2Score} ${game.team2} (Winner: ${winner})`);
    return true;
  }

  // Update player status based on court assignments
  updatePlayerStatus(courts) {
    // Reset all players to waiting
    Object.values(this.playerStatus).forEach(status => {
      if (status.status === 'playing') {
        status.status = 'waiting';
        status.currentCourt = null;
      }
    });

    // Update players currently on courts
    courts.forEach(court => {
      if (court.isActive && court.currentGame) {
        const allPlayers = [
          ...court.currentGame.players.team1,
          ...court.currentGame.players.team2
        ];
        
        allPlayers.forEach(displayName => {
          // Find full name from display name
          const fullName = Object.keys(this.getDisplayNameMap()).find(key => 
            this.getDisplayNameMap()[key] === displayName
          );
          const playerName = fullName || displayName;
          
          if (this.playerStatus[playerName]) {
            this.playerStatus[playerName].status = 'playing';
            this.playerStatus[playerName].currentCourt = court.id;
          }
        });
      }
    });
  }

  // Helper to get display name mapping
  getDisplayNameMap() {
    const allPlayerNames = Object.values(this.teamData).reduce((names, team) => {
      return [
        ...names,
        team.m1.split('(')[0].trim(),
        team.m2.split('(')[0].trim(),
        team.f1.split('(')[0].trim(),
        team.f2.split('(')[0].trim()
      ];
    }, []);

    const firstNameCounts = {};
    const nameMap = {};
    
    allPlayerNames.forEach(fullName => {
      const firstName = fullName.split(' ')[0];
      firstNameCounts[firstName] = (firstNameCounts[firstName] || 0) + 1;
    });

    allPlayerNames.forEach(fullName => {
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts[nameParts.length - 1];
      
      if (firstNameCounts[firstName] > 1) {
        nameMap[fullName] = `${firstName} ${lastName.charAt(0)}`;
      } else {
        nameMap[fullName] = firstName;
      }
    });

    return nameMap;
  }

  // Calculate tournament progress
  getTournamentProgress() {
    const completedGames = this.games.filter(g => g.status === 'completed').length;
    const progressPercentage = Math.round((completedGames / this.games.length) * 100);

    return {
      completedGames,
      totalGames: this.games.length, // FIXED: Use this.games.length instead of unused totalGames variable
      progressPercentage,
      inProgress: this.games.filter(g => g.status === 'in-progress').length,
      remaining: this.games.filter(g => g.status === 'pending').length
    };
  }

  // Calculate team standings
  calculateStandings() {
    const standings = Object.keys(this.teamData).map(teamId => {
      const teamGames = this.games.filter(g => 
        (g.team1 === teamId || g.team2 === teamId) && g.status === 'completed'
      );

      let wins = 0;
      let losses = 0;
      let pointsFor = 0;
      let pointsAgainst = 0;

      teamGames.forEach(game => {
        const isTeam1 = game.team1 === teamId;
        const teamScore = isTeam1 ? game.result.team1Score : game.result.team2Score;
        const opponentScore = isTeam1 ? game.result.team2Score : game.result.team1Score;

        pointsFor += teamScore;
        pointsAgainst += opponentScore;

        if (game.result.winner === teamId) {
          wins++;
        } else {
          losses++;
        }
      });

      const gamesPlayed = wins + losses;
      const winPercentage = gamesPlayed > 0 ? Math.round((wins / gamesPlayed) * 100) : 0;
      const pointDifferential = pointsFor - pointsAgainst;

      return {
        team: teamId,
        teamName: this.teamData[teamId].name,
        wins,
        losses,
        gamesPlayed,
        winPercentage,
        pointsFor,
        pointsAgainst,
        pointDifferential
      };
    });

    // Sort by wins (descending), then by point differential (descending)
    return standings.sort((a, b) => {
      if (a.wins !== b.wins) return b.wins - a.wins;
      return b.pointDifferential - a.pointDifferential;
    });
  }

  // Get remaining games breakdown
  getRemainingGamesBreakdown() {
    const completedGames = this.games.filter(g => g.status === 'completed').length;
    const remainingGames = this.games.filter(g => g.status === 'pending');

    const breakdown = {
      'MM': { count: 0, games: [] },
      'FF': { count: 0, games: [] },
      'MX1': { count: 0, games: [] },
      'MX2': { count: 0, games: [] }
    };

    remainingGames.forEach(game => {
      breakdown[game.gameType].count++;
      breakdown[game.gameType].games.push(game);
    });

    return {
      completed: completedGames,
      total: remainingGames.length,
      breakdown
    };
  }

  // Check if tournament is complete
  isTournamentComplete() {
    return this.games.every(game => game.status === 'completed');
  }

  // Get tournament results
  getTournamentResults() {
    const standings = this.calculateStandings();
    const topTeams = standings.slice(0, 2);
    
    // Check if there's a tie for first place
    const needsPlayoff = topTeams.length >= 2 && 
                        topTeams[0].wins === topTeams[1].wins && 
                        topTeams[0].pointDifferential === topTeams[1].pointDifferential;

    return {
      standings,
      champion: standings[0],
      needsPlayoff,
      playoffTeams: needsPlayoff ? topTeams : null,
      tournamentStats: this.getTournamentProgress()
    };
  }

  // Championship Final Methods
  initializeChampionshipFinal(team1Id, team2Id) {
    this.championshipFinal = {
      initialized: true,
      completed: false,
      team1: team1Id,
      team2: team2Id,
      score: { team1: 0, team2: 0 },
      winner: null,
      gameTypes: ['FF', 'MM', 'MX1', 'MX2'],
      currentGameTypeIndex: 0,
      pointsUntilRotation: 4
    };

    console.log(`Championship Final initialized: ${team1Id} vs ${team2Id}`);
  }

  getCurrentFinalPairs() {
    if (!this.championshipFinal.initialized) return null;

    const currentGameType = this.championshipFinal.gameTypes[this.championshipFinal.currentGameTypeIndex];
    const gameTypeLabels = {
      'FF': "Women's Doubles",
      'MM': "Men's Doubles", 
      'MX1': 'Mixed Doubles (M1+F1)',
      'MX2': 'Mixed Doubles (M2+F2)'
    };

    const playerPositions = {
      'FF': ['f1', 'f2'],
      'MM': ['m1', 'm2'],
      'MX1': ['m1', 'f1'], 
      'MX2': ['m2', 'f2']
    };

    const positions = playerPositions[currentGameType];
    
    return {
      gameType: currentGameType,
      gameTypeLabel: gameTypeLabels[currentGameType],
      pointsUntilRotation: this.championshipFinal.pointsUntilRotation,
      score: this.championshipFinal.score,
      team1: {
        teamId: this.championshipFinal.team1,
        players: positions.map(pos => this.teamData[this.championshipFinal.team1][pos])
      },
      team2: {
        teamId: this.championshipFinal.team2,
        players: positions.map(pos => this.teamData[this.championshipFinal.team2][pos])
      }
    };
  }

  recordFinalPoint(winningTeamId) {
    if (!this.championshipFinal.initialized || this.championshipFinal.completed) return;

    // Increment score
    if (winningTeamId === this.championshipFinal.team1) {
      this.championshipFinal.score.team1++;
    } else {
      this.championshipFinal.score.team2++;
    }

    // Decrement rotation counter
    this.championshipFinal.pointsUntilRotation--;

    // Check for rotation
    if (this.championshipFinal.pointsUntilRotation <= 0) {
      this.championshipFinal.currentGameTypeIndex = 
        (this.championshipFinal.currentGameTypeIndex + 1) % this.championshipFinal.gameTypes.length;
      this.championshipFinal.pointsUntilRotation = 4;
    }

    // Check for win condition (first to 21, win by 2)
    const score1 = this.championshipFinal.score.team1;
    const score2 = this.championshipFinal.score.team2;
    
    if ((score1 >= 21 || score2 >= 21) && Math.abs(score1 - score2) >= 2) {
      this.championshipFinal.completed = true;
      this.championshipFinal.winner = score1 > score2 ? 
        this.championshipFinal.team1 : this.championshipFinal.team2;
      
      console.log(`Championship Final completed! Winner: ${this.championshipFinal.winner}`);
    }
  }
}